/**
 * Application Router
 *
 * Defines all routes for the application with role-based access control.
 */

import { createBrowserRouter, Navigate } from 'react-router-dom';

// Layouts
import AdminLayout from './components/layouts/AdminLayout';
import CrewLayout from './components/layouts/CrewLayout';
import PlatformLayout from './components/layouts/PlatformLayout';
import ProtectedRoute from './components/layouts/ProtectedRoute';

// Auth Pages
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import SetupAccount from './pages/auth/SetupAccount';
import Signup from './pages/auth/Signup';
import VerifyEmail from './pages/auth/VerifyEmail';

// Public Pages
import LandingPage from './pages/LandingPage';
import LegalLayout from './pages/public/LegalLayout';
// Legal prose is eagerly imported, not lazy: compliance reviewers (A2P 10DLC
// vetting, payment processors) fetch these URLs directly and the content must
// render without a second chunk round-trip.
import PrivacyPolicy from './content/legal/privacy.mdx';
import TermsOfService from './content/legal/terms.mdx';
import SmsOptInPage from './pages/public/SmsOptInPage';
import JoinPage from './pages/public/JoinPage';
import DashboardRedirect from './pages/DashboardRedirect';
import DigestUnsubscribePage from './pages/public/DigestUnsubscribePage';

// Documentation Pages
import DocsLayout from './pages/docs/DocsLayout';
import DocsHome from './pages/docs/DocsHome';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import AdminEvents from './pages/admin/Events';
import AdminSettings from './pages/admin/Settings';
import EventTypesPage from './pages/admin/settings/EventTypesPage';
import OrganizationSettings from './pages/admin/settings/OrganizationSettings';
import NotificationSettings from './pages/admin/settings/NotificationSettings';
import Integrations from './pages/admin/settings/Integrations';
import BillingSettings from './pages/admin/settings/BillingSettings';
import CreateEventPage from './pages/admin/events/CreateEventPage';
import EventDetailPage from './pages/admin/events/EventDetailPage';
import EditEventPage from './pages/admin/events/EditEventPage';
import { CrewListPage, CreateCrewPage, CrewDetailPage, EditCrewPage } from './pages/admin/crew';
import { MessagesPage as AdminMessagesPage, MessageDetailPage as AdminMessageDetailPage } from './pages/admin/messages';
import { PayPeriodsPage, PayPeriodDetailPage } from './pages/admin/payPeriods';
import {
  ReportsPage,
  PayrollReportPage,
  PayrollExportPage,
  TimesheetExportPage,
  CrewByEventPage,
  EventCostsPage,
  CrewUtilizationPage,
  StaffingStatusPage,
  CostsBySportPage,
  CostsByEventTypePage,
  AvailabilityAnalyticsPage,
  AssignmentResponseRatesPage,
  CrewPaymentHistoryPage,
  CrewTimesheetPage,
} from './pages/admin/reports';

// Crew Pages
import CrewDashboard from './pages/crew/Dashboard';
import MyAssignmentsPage from './pages/crew/MyAssignmentsPage';
import ProfilePage from './pages/crew/ProfilePage';
import ResourcesPage from './pages/crew/ResourcesPage';
import CrewEventDetailPage from './pages/crew/EventDetailPage';
import { InboxPage as CrewInboxPage, MessageDetailPage as CrewMessageDetailPage } from './pages/crew/messages';

// Platform Admin Pages
import PlatformDashboard from './pages/platform/Dashboard';
import OrganizationDetailPage from './pages/platform/OrganizationDetailPage';
import PlatformAccountPage from './pages/platform/AccountPage';

export const router = createBrowserRouter([
  // =============================================================================
  // Public Routes (no authentication required)
  // =============================================================================
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPassword />,
  },
  {
    path: '/reset-password',
    element: <ResetPassword />,
  },
  {
    path: '/setup-account',
    element: <SetupAccount />,
  },
  {
    path: '/signup',
    element: <Signup />,
  },
  {
    path: '/verify-email',
    element: <VerifyEmail />,
  },
  {
    // External weekly-digest recipients (no account) land here from the
    // unsubscribe link in the email.
    path: '/digest/unsubscribe/:token',
    element: <DigestUnsubscribePage />,
  },
  {
    // The role-agnostic dashboard entry every email links to. Not itself a
    // ProtectedRoute: it renders its own redirect for signed-out visitors so
    // the destination is decided in one place.
    path: '/dashboard',
    element: <DashboardRedirect />,
  },

  // =============================================================================
  // Admin Routes (ADMIN or SUPER_ADMIN role required)
  // =============================================================================
  {
    path: '/admin',
    element: (
      <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
        <AdminLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <AdminDashboard />,
      },
      {
        path: 'events',
        element: <AdminEvents />,
      },
      {
        path: 'events/new',
        element: <CreateEventPage />,
      },
      {
        path: 'events/:id',
        element: <EventDetailPage />,
      },
      {
        path: 'events/:id/edit',
        element: <EditEventPage />,
      },
      {
        path: 'crew',
        element: <CrewListPage />,
      },
      {
        path: 'crew/new',
        element: <CreateCrewPage />,
      },
      {
        path: 'crew/:id',
        element: <CrewDetailPage />,
      },
      {
        path: 'crew/:id/edit',
        element: <EditCrewPage />,
      },
      {
        path: 'settings',
        element: <AdminSettings />,
      },
      {
        // Personal settings, same page crew sees at /crew/profile, but inside
        // the admin chrome so admins/owners don't get bounced into the
        // mobile-first crew layout to change their own preferences.
        path: 'profile',
        element: <ProfilePage />,
      },
      {
        path: 'settings/event-types',
        element: <EventTypesPage />,
      },
      {
        path: 'settings/organization',
        element: <OrganizationSettings />,
      },
      {
        path: 'settings/notifications',
        element: <NotificationSettings />,
      },
      {
        path: 'settings/integrations',
        element: <Integrations />,
      },
      {
        path: 'settings/billing',
        element: <BillingSettings />,
      },
      {
        path: 'messages',
        element: <AdminMessagesPage />,
      },
      {
        path: 'messages/:id',
        element: <AdminMessageDetailPage />,
      },
      {
        path: 'resources',
        element: <ResourcesPage />,
      },
      {
        path: 'pay-periods',
        element: <PayPeriodsPage />,
      },
      {
        path: 'pay-periods/:id',
        element: <PayPeriodDetailPage />,
      },
      {
        path: 'reports',
        element: <ReportsPage />,
      },
      {
        path: 'reports/payroll',
        element: <PayrollReportPage />,
      },
      {
        path: 'reports/payroll-export',
        element: <PayrollExportPage />,
      },
      {
        path: 'reports/timesheet-export',
        element: <TimesheetExportPage />,
      },
      {
        path: 'reports/crew-by-event',
        element: <CrewByEventPage />,
      },
      {
        path: 'reports/event-costs',
        element: <EventCostsPage />,
      },
      {
        path: 'reports/crew-utilization',
        element: <CrewUtilizationPage />,
      },
      {
        path: 'reports/staffing-status',
        element: <StaffingStatusPage />,
      },
      {
        path: 'reports/costs-by-sport',
        element: <CostsBySportPage />,
      },
      {
        path: 'reports/costs-by-event-type',
        element: <CostsByEventTypePage />,
      },
      {
        path: 'reports/availability-analytics',
        element: <AvailabilityAnalyticsPage />,
      },
      {
        path: 'reports/assignment-response-rates',
        element: <AssignmentResponseRatesPage />,
      },
      {
        path: 'reports/crew/:id/history',
        element: <CrewPaymentHistoryPage />,
      },
      {
        path: 'reports/crew/:id/timesheet',
        element: <CrewTimesheetPage />,
      },
    ],
  },

  // =============================================================================
  // Platform Admin Routes (PLATFORM_ADMIN only)
  // =============================================================================
  {
    path: '/platform',
    element: (
      <ProtectedRoute allowedRoles={['PLATFORM_ADMIN']}>
        <PlatformLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <PlatformDashboard />,
      },
      {
        path: 'organizations/:id',
        element: <OrganizationDetailPage />,
      },
      {
        path: 'account',
        element: <PlatformAccountPage />,
      },
    ],
  },

  // =============================================================================
  // Crew Routes (any authenticated user)
  // =============================================================================
  {
    path: '/crew',
    element: (
      <ProtectedRoute allowedRoles={['CREW', 'ADMIN', 'SUPER_ADMIN']}>
        <CrewLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <CrewDashboard />,
      },
      {
        path: 'assignments',
        element: <MyAssignmentsPage />,
      },
      {
        path: 'resources',
        element: <ResourcesPage />,
      },
      {
        path: 'profile',
        element: <ProfilePage />,
      },
      {
        path: 'events/:id',
        element: <CrewEventDetailPage />,
      },
      {
        path: 'messages',
        element: <CrewInboxPage />,
      },
      {
        path: 'messages/:id',
        element: <CrewMessageDetailPage />,
      },
      // Future routes:
      // { path: 'availability', element: <Availability /> },
    ],
  },

  // =============================================================================
  // Public Routes
  // =============================================================================
  {
    path: '/',
    element: <LandingPage />,
  },
  {
    // QR crew signup — what a scanned code opens. Public, mobile-first.
    path: '/join/:token',
    element: <JoinPage />,
  },

  // =============================================================================
  // Legal Routes (public, no auth required)
  //
  // These URLs are submitted to The Campaign Registry as the campaign's
  // PrivacyPolicyUrl and TermsAndConditionsUrl and are vetted by a human.
  // They must stay at exactly /privacy and /terms, must never require a login,
  // and must never redirect.
  // =============================================================================
  {
    element: <LegalLayout />,
    children: [
      { path: '/privacy', element: <PrivacyPolicy /> },
      { path: '/terms', element: <TermsOfService /> },
      // Registered with TCR as the campaign's opt-in documentation URL
      // (error 30896 remediation) — same rules as /privacy and /terms.
      { path: '/sms-opt-in', element: <SmsOptInPage /> },
    ],
  },

  // =============================================================================
  // Documentation Routes (public, no auth required)
  // =============================================================================
  {
    path: '/docs',
    element: <DocsLayout />,
    children: [
      {
        index: true,
        element: <DocsHome />,
      },
      // Lazy load content pages to reduce bundle size
      {
        path: 'getting-started/quick-start',
        lazy: async () => {
          const { default: QuickStart } = await import('./content/docs/getting-started/quick-start.mdx');
          return { element: <QuickStart /> };
        },
      },
      {
        path: 'getting-started/concepts',
        lazy: async () => {
          const { default: Concepts } = await import('./content/docs/getting-started/concepts.mdx');
          return { element: <Concepts /> };
        },
      },
      // Org Owner Guides
      {
        path: 'guides/org-owners/setup-organization',
        lazy: async () => {
          const { default: Content } = await import('./content/docs/guides/org-owners/setup-organization.mdx');
          return { element: <Content /> };
        },
      },
      {
        path: 'guides/org-owners/manage-members',
        lazy: async () => {
          const { default: Content } = await import('./content/docs/guides/org-owners/manage-members.mdx');
          return { element: <Content /> };
        },
      },
      {
        path: 'guides/org-owners/billing',
        lazy: async () => {
          const { default: Content } = await import('./content/docs/guides/org-owners/billing.mdx');
          return { element: <Content /> };
        },
      },
      // Admin Guides
      {
        path: 'guides/admins/events',
        lazy: async () => {
          const { default: Content } = await import('./content/docs/guides/admins/events.mdx');
          return { element: <Content /> };
        },
      },
      {
        path: 'guides/admins/crew',
        lazy: async () => {
          const { default: Content } = await import('./content/docs/guides/admins/crew.mdx');
          return { element: <Content /> };
        },
      },
      {
        path: 'guides/admins/assignments',
        lazy: async () => {
          const { default: Content } = await import('./content/docs/guides/admins/assignments.mdx');
          return { element: <Content /> };
        },
      },
      {
        path: 'guides/admins/messaging',
        lazy: async () => {
          const { default: Content } = await import('./content/docs/guides/admins/messaging.mdx');
          return { element: <Content /> };
        },
      },
      {
        path: 'guides/admins/documents',
        lazy: async () => {
          const { default: Content } = await import('./content/docs/guides/admins/documents.mdx');
          return { element: <Content /> };
        },
      },
      {
        path: 'guides/admins/resources',
        lazy: async () => {
          const { default: Content } = await import('./content/docs/guides/admins/resources.mdx');
          return { element: <Content /> };
        },
      },
      {
        path: 'guides/admins/pay-periods',
        lazy: async () => {
          const { default: Content } = await import('./content/docs/guides/admins/pay-periods.mdx');
          return { element: <Content /> };
        },
      },
      {
        path: 'guides/admins/reports',
        lazy: async () => {
          const { default: Content } = await import('./content/docs/guides/admins/reports.mdx');
          return { element: <Content /> };
        },
      },
      // API Reference
      {
        path: 'api',
        lazy: async () => {
          const { default: Content } = await import('./content/docs/api/index.mdx');
          return { element: <Content /> };
        },
      },
      {
        path: 'api/authentication',
        lazy: async () => {
          const { default: Content } = await import('./content/docs/api/authentication.mdx');
          return { element: <Content /> };
        },
      },
      {
        path: 'api/events',
        lazy: async () => {
          const { default: Content } = await import('./content/docs/api/events.mdx');
          return { element: <Content /> };
        },
      },
      {
        path: 'api/assignments',
        lazy: async () => {
          const { default: Content } = await import('./content/docs/api/assignments.mdx');
          return { element: <Content /> };
        },
      },
      {
        path: 'api/users',
        lazy: async () => {
          const { default: Content } = await import('./content/docs/api/users.mdx');
          return { element: <Content /> };
        },
      },
      {
        path: 'api/messages',
        lazy: async () => {
          const { default: Content } = await import('./content/docs/api/messages.mdx');
          return { element: <Content /> };
        },
      },
    ],
  },

  // =============================================================================
  // Fallback
  // =============================================================================
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);
